import { t } from 'i18next';
import { isBrowser } from './browserHelper';
import { COOKIE_TO_IKEAAPP, setCookie } from './cookieHelper';

export type NativeAppParsedQueryParams = {
  ikeaTheme: string;
  testMode: string;
  platform: string;
  redirectUrl: string;
};

export type NativeAppQueryParamKeys =
  | 'ikea_theme'
  | 'test_mode'
  | 'platform'
  | 'redirect_url';

export interface NativeAppExclude {
  ikeaTheme?: boolean;
  testMode?: boolean;
  platform?: boolean;
  redirectUrl?: boolean;
}

const NATIVE_APP_QUERY_PARAMS = {
  IKEA_THEME: 'ikea_theme',
  TEST_MODE: 'test_mode',
  PLATFORM: 'platform',
  REDIRECT_URL: 'redirect_url',
};

const DEFAULT_NATIVE_APP_PARSED_QUERY_PARAMS = {
  ikeaTheme: '',
  testMode: '',
  platform: '',
  redirectUrl: '',
};

const NATIVE_APP_DARK_THEME_KEY_VALUE = 'ikea_theme=dark';
const NATIVE_APP_DARK_THEME_ATTRIBUTE = 'ikea-theme-dark';
export const NATIVE_APP_OPERATION = 'ikeaapp';

export const setNativeAppTheme = () => {
  // This could be improved by moving the logic to set the attribute from an
  // inline script directly in the static html file. The current solution
  // applies dark mode after the javascript has loaded. Using an inline script
  // in the html file would apply dark mode directly after the html has loaded.
  if (!isBrowser()) return;
  if (
    window.location.pathname.includes(NATIVE_APP_OPERATION) &&
    window.location.search.includes(NATIVE_APP_DARK_THEME_KEY_VALUE)
  ) {
    document.body.setAttribute(NATIVE_APP_DARK_THEME_ATTRIBUTE, '');
  } else {
    document.body.removeAttribute(NATIVE_APP_DARK_THEME_ATTRIBUTE);
  }
};

export const isNativeAppOperation = (operation: string) => {
  return operation === NATIVE_APP_OPERATION;
};

export const isNativeApp = () => {
  return isBrowser() && window.location.href.includes(NATIVE_APP_OPERATION);
};

export const getNativeAppOperation = () => {
  return isNativeApp() ? NATIVE_APP_OPERATION : '';
};

export const getNativeAppPath = (path: string) => {
  if (isNativeApp()) {
    return `${NATIVE_APP_OPERATION}${path}`;
  }
  return path;
};

export const getNativeAppQueryParams = (): NativeAppParsedQueryParams => {
  if (isNativeApp()) {
    const queryParams = new URLSearchParams(window.location.search);
    const { IKEA_THEME, TEST_MODE, PLATFORM, REDIRECT_URL } =
      NATIVE_APP_QUERY_PARAMS;
    const ikeaTheme = queryParams.get(IKEA_THEME) || '';
    const testMode = queryParams.get(TEST_MODE) || '';
    const platform = queryParams.get(PLATFORM) || '';
    const redirectUrl = queryParams.get(REDIRECT_URL) || '';
    return { ikeaTheme, testMode, platform, redirectUrl };
  }
  return DEFAULT_NATIVE_APP_PARSED_QUERY_PARAMS;
};

export const getNativeAppQueryParamNames = () => {
  return isNativeApp() ? Object.values(NATIVE_APP_QUERY_PARAMS) : [];
};

const appendRedirectUrl = (
  defaultRedirectUrl: string,
  redirectUrl: string,
  links: string[],
): string[] => {
  if (redirectUrl.includes('http')) {
    links.push(redirectUrl);
    return links;
  }

  if (defaultRedirectUrl.includes('http')) {
    links.push(defaultRedirectUrl);
    return links;
  }
  return links;
};

const getOperationAllowedRedirects = (
  operation: string,
  isPrivacyPolicyVisible: boolean,
) => {
  const links: string[] = [];
  if (isPrivacyPolicyVisible) {
    links.push(t('pages.contact.privacyPolicy.link'));
  }
  const PrivacyPolicyWithTermsAndConditionsTranslationKey =
    'pages.contact.privacyPolicyWithTermsAndConditions';
  return appendRedirectUrl(
    t(`${PrivacyPolicyWithTermsAndConditionsTranslationKey}.link.default`),
    t(`${PrivacyPolicyWithTermsAndConditionsTranslationKey}.link.${operation}`),
    [...links],
  );
};

export const setCookieForNativeApp = (
  orderNumber: string,
  operation: string,
  isPrivacyPolicyVisible: boolean,
) => {
  if (isNativeApp()) {
    setCookie(COOKIE_TO_IKEAAPP, {
      orderNumber,
      allowedRedirects: getOperationAllowedRedirects(
        operation,
        isPrivacyPolicyVisible,
      ),
    });
  }
};
